/* eslint-disable no-console */
/* eslint-disable react/no-children-prop */
import {
  makeStyles,
  RACModal,
  Typography,
  RACButton,
  Grid,
  CircularProgress,
  RACCOLOR,
  RACSelect,
  TextField,
} from '@rentacenter/racstrap';
import { useLocation } from 'react-router';
import { storeException } from '../../../api/store';
import React, { useState, useEffect } from 'react';
import {
  CustomerLocationState,
  Option,
  ReferenceKeys,
} from '../../../types/types';
import {
  getSelectedStore,
  mapReferenceResponse,
  pipe,
} from '../../../utils/utils';
import { getReference } from '../../../api/reference';
import {
  API_ERROR_MESSAGE,
  CACHED_KEYS,
  SELECT_ONE_OPTION,
  TEXT_MAX_LENGTH,
} from '../../../constants/constants';
import i18n from 'i18next'; // Import i18next

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  onError: () => void;
  onSave: () => void;
}

export const useStyles = makeStyles((theme: any) => ({
  dialogContent: {
    marginBottom: '2rem',
  },
  dialogRoot: {
    '& .MuiPaper-rounded': {
      borderRadius: theme.typography.pxToRem(12),
    },
    '& .MuiDialog-paperWidthXs': {
      maxWidth: theme.typography.pxToRem(500),
    },
    '& .MuiTypography-h5': {
      fontSize: theme.typography.pxToRem(20),
      fontWeight: 500,
      lineHeight: theme.typography.pxToRem(30),
    },
    '& .MuiTypography-h6': {
      fontFamily: 'OpenSans-semiBold',
      lineHeight: theme.typography.pxToRem(20),
    },
  },
  notesLabel: {
    color: '#212529',
    padding: '0px 0px 15px',
  },
  fieldVisitTextContent: {
    borderRadius: '5px',
    width: theme.typography.pxToRem(450),
  },
  callResultWrapper: {
    width: '75%',
  },
  callResultOptionsPaper: {
    maxHeight: theme.typography.pxToRem(150),
  },
  notesCharacterCount: {
    marginTop: theme.typography.pxToRem(5),
  },
  masterLoader: {
    position: 'fixed',
    backgroundColor: `${RACCOLOR.WASHED_BLACK}`,
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    opacity: 0.6,
    zIndex: 1050,
    textAlign: 'center',
    margin: 0,
  },
  Loader: {
    display: 'block',
    position: 'fixed',
    zIndex: 1051,
    top: '50%',
    right: '50%',
  },
  popupButn: {
    display: 'flex',
    gap: '10px',
    marginRight: '30%',
  },
}));

export const RecordCheckRequestModal: React.FC<ModalProps> = ({
  open,
  onClose,
  onError,
  onSave,
}) => {
  const classes = useStyles();
  const location = useLocation<CustomerLocationState>();
  const [loader, setloader] = useState(false);
  const [recordCheckOptions, setRecordCheckOptions] = useState<Option[]>([]);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [apiError, setApiError] = useState(false);

  const customerId =
    location?.state?.customer?.customerId || location?.pathname?.split('/')[3];

  const addSelectOneOption = (arrayWithOptions: any[]) => {
    if (!arrayWithOptions) {
      return [
        {
          label: i18n.t(SELECT_ONE_OPTION),
          value: '',
        },
      ];
    }

    return [
      {
        label: i18n.t(SELECT_ONE_OPTION),
        value: '',
      },
      ...arrayWithOptions,
    ];
  };

  useEffect(() => {
    setloader(true);
    getReference(
      [ReferenceKeys.RECORD_CHECK_REQUEST_REASON],
      CACHED_KEYS.RECORD_CHECK_REQUEST_REASON_KEY
    )
      .then((response: any) => {
        pipe(
          mapReferenceResponse,
          addSelectOneOption,
          setRecordCheckOptions
        )(response?.references[0]?.referenceDetails);
      })
      .catch((e) => {
        console.error('referenceDetails error', e);
        setApiError(true);
      })
      .finally(() => {
        setloader(false);
      });
  }, []);

  const NextBtnClick = async () => {
    try {
      setloader(true);
      const selectedOption = recordCheckOptions.find(
        (opt) => opt.value === reason
      );
      const exceptionLabel = selectedOption?.label;
      const exceptionText = note
        ? `${exceptionLabel}: ${note}`
        : exceptionLabel;

      const payload: any = {
        storeException: [
          {
            identifierId: Number(customerId),
            identifierType: 'CUSTOMER',
            storeNumber: getSelectedStore(),
            exceptionTypeRefCode: 'STRR',
            exceptionText: exceptionText,
          },
        ],
      };
      const res = await storeException(payload);
      if (res && res[0].storeExceptionId) {
        setloader(false);
        onSave();
      }
    } catch (e) {
      console.error('Store Exception error:', e);
      setloader(false);
      onError();
    }
  };

  const RecordCheckRequestModalContent = () => {
    return (
      <>
        <div className={classes.callResultWrapper}>
          <RACSelect
            classes={{ paper: classes.callResultOptionsPaper }}
            inputLabel="Reason"
            isDisabled={loader}
            defaultValue={reason}
            loading={loader && !recordCheckOptions.length}
            options={recordCheckOptions}
            onChange={(e: React.ChangeEvent<{ value: any }>) =>
              setReason(e.target.value)
            }
            {...(apiError && {
              errorMessage: API_ERROR_MESSAGE,
            })}
          />
        </div>
        {reason == 'OTHR' && (
          <>
            <Typography variant="h6" className={classes.notesLabel}>
              Reason
            </Typography>
            <TextField
              className={classes.fieldVisitTextContent}
              multiline
              autoFocus
              variant="outlined"
              maxRows={3}
              minRows={3}
              value={note}
              onChange={(e) => {
                if (note.length >= TEXT_MAX_LENGTH) {
                  const trimmedNote = e.target.value?.substring(
                    0,
                    TEXT_MAX_LENGTH
                  );
                  setNote(trimmedNote);
                } else {
                  setNote(e.target.value);
                }
              }}
            />
            <span className={classes.notesCharacterCount}>
              ({TEXT_MAX_LENGTH}/{note.length})
            </span>
          </>
        )}
      </>
    );
  };

  return (
    <div>
      {loader === true ? (
        <Grid className={classes.masterLoader}>
          <Grid className={classes.Loader}>
            <CircularProgress />
          </Grid>
        </Grid>
      ) : null}
      <RACModal
        isOpen={open}
        classes={{
          dialogContent: classes.dialogContent,
          dialog: classes.dialogRoot,
        }}
        maxWidth="xs"
        title="Record Check Request"
        content={RecordCheckRequestModalContent()}
        onClose={() => onClose()}
        buttons={
          <div className={classes.popupButn}>
            <RACButton
              variant="outlined"
              color="secondary"
              onClick={() => onClose()}
              disabled={loader}
            >
              Cancel
            </RACButton>
            <RACButton
              variant="contained"
              color="primary"
              onClick={NextBtnClick}
              disabled={!reason.length || loader}
              loading={loader}
            >
              Save
            </RACButton>
          </div>
        }
      />
    </div>
  );
};
